import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { McpToolCall } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { issueKindLabel, issuePath } from "@/lib/issues";

import { formatDuration } from "../utils";

/** The Odoo figures of a call, in the order the detail lists them. */
const ODOO_FIGURES = [
  "odoo_requests",
  "odoo_ms",
  "odoo_max_ms",
  "worker_wait_ms",
  "spacing_wait_ms",
  "retry_wait_ms",
  "retries",
  "rate_limits",
  "odoo_status",
] as const satisfies readonly (keyof McpToolCall)[];

type OdooFigure = (typeof ODOO_FIGURES)[number];

/** A figure in its unit: a time as "1,2 s", a count as "3", Odoo's answer as its HTTP status. */
function figureText(figure: OdooFigure, value: number): string {
  if (figure.endsWith("_ms")) {
    return formatDuration(value);
  }
  return figure === "odoo_status" ? String(value) : formatNumber(value);
}

/**
 * One call's details, under its row in the call log: how it ended (a
 * failure's kind, with the way to its issue), and what it did in Odoo: its
 * requests, the time in Odoo and the slowest request, the three waits, the
 * new attempts, how often Odoo slowed it down, and Odoo's last answer. A call
 * that never went to Odoo, or one from before the figures were kept, says so.
 */
export function CallDetail({ call }: { call: McpToolCall }) {
  const { t } = useTranslation();
  const figures = ODOO_FIGURES.flatMap((figure) => {
    const value = call[figure];
    return value === null || value === undefined ? [] : [{ figure, value }];
  });
  const duration = call.duration_ms != null && (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">{t("analytics.duration")}</dt>
      <dd className="text-foreground font-medium whitespace-nowrap tabular-nums">
        {formatDuration(call.duration_ms)}
      </dd>
    </div>
  );
  return (
    <div className="space-y-3 text-sm">
      {!call.ok && call.error_type && (
        <p className="text-foreground">
          {t("analytics.detail.failure")}{" "}
          <span className="font-medium">{issueKindLabel(call.error_type)}</span>{" "}
          <code className="text-muted font-mono text-xs">{call.error_type}</code>
          {call.issue_key && (
            <>
              {" "}
              <Link
                to={issuePath(call.issue_key)}
                className="text-accent font-medium whitespace-nowrap hover:underline"
              >
                {t("analytics.detail.toIssue")}
              </Link>
            </>
          )}
        </p>
      )}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {duration}
        {figures.map(({ figure, value }) => (
          <div key={figure} className="flex items-baseline justify-between gap-3">
            <dt className="text-muted">{t(`analytics.detail.figures.${figure}`)}</dt>
            <dd className="text-foreground font-medium whitespace-nowrap tabular-nums">
              {figureText(figure, value)}
            </dd>
          </div>
        ))}
      </dl>
      {figures.length === 0 && <p className="text-muted">{t("analytics.detail.noOdoo")}</p>}
    </div>
  );
}
